// Per-point yield for the Tracks Details panel.
//
// The rules come from the server (GET /devices/:id/tracks?yield=1): each point
// carries `yieldAmount` (its mass signal under the device's sensor rule, null
// when not harvesting), `coeff` (the calibration in force) and `widthM` (the
// header width in force), and the response carries the speed band. They are the
// backend's own rules (lib/harvestRules.ts, services/deviceYieldSettings.ts) —
// the panel used to re-implement them with hard-coded limits and disagreed
// with Analytics for static potato machines and admin-tuned devices. This file
// only applies the band and sums; the sums stay client-side so the zoom window
// and the typed coefficient / max-speed overrides keep working.

/** Response-level gates from GET /devices/:id/tracks?yield=1. */
export type YieldGates = {
  /** Admin working-speed band, km/h. */
  minSpeed: number;
  maxSpeed: number;
  /** Static potato machine: no speed gate; area = virtual speed × dt × width. */
  staticPotato: boolean;
  virtualSpeedMs: number;
  /** At least one GreenGrowth calibration coefficient is in force. */
  hasCalibration: boolean;
  /** devices.source ("greengrowth" | "johndeere"); absent from older backends. */
  source?: string | null;
};

export type PanelPoint = {
  lat: number;
  lng: number;
  rawSpeed?: number | null;
  /** Seconds since the previous reading. */
  timeDiff: number | null;
  yieldAmount?: number | null;
  coeff?: number | null;
  widthM?: number | null;
};

export type PointYield = { kg: number; areaM2: number };

/** A step this long is a pause, not travel (backend MAX_GAP_SECONDS). */
const MAX_STEP_SECONDS = 300;
/** Longest step credited as travel (backend lib/harvestPath.ts MAX_STEP_METERS). */
const MAX_STEP_METERS = 15;

/** A combine imported from John Deere (backend harvestRules isJohnDeereSource). */
export const isJohnDeereGates = (gates: YieldGates | null | undefined): boolean =>
  gates?.source === "johndeere";

/** Great-circle metres between two fixes, clipped like the backend's stepMeters. */
export function stepMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const toRad = Math.PI / 180;
  const dLat = (lat2 - lat1) * toRad;
  const dLng = (lng2 - lng1) * toRad;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLng / 2) ** 2;
  return Math.min(2 * R * Math.asin(Math.min(1, Math.sqrt(a))), MAX_STEP_METERS);
}

/** A real position: the API sends a missing one as 0,0. */
const hasPosition = (p: PanelPoint): boolean =>
  Number.isFinite(p.lat) &&
  Number.isFinite(p.lng) &&
  !(Math.abs(p.lat) < 0.001 && Math.abs(p.lng) < 0.001);

/**
 * Mass and covered area per point, aligned with `data`; null = not counted.
 * `maxSpeedOverride` / `coefficientOverride` are the panel's session-only
 * overrides of the server's band maximum and calibration. `johnDeere` is the
 * device list's source, for a response whose gates do not carry it.
 */
export function computePointYields(
  data: PanelPoint[],
  gates: YieldGates,
  opts: {
    coefficientOverride?: number | null;
    maxSpeedOverride?: number | null;
    johnDeere?: boolean;
  } = {}
): Array<PointYield | null> {
  if (opts.johnDeere || isJohnDeereGates(gates)) {
    return computeJohnDeereYields(data, opts.coefficientOverride);
  }

  const maxSpeed = opts.maxSpeedOverride ?? gates.maxSpeed;
  const out: Array<PointYield | null> = new Array(data.length).fill(null);
  let prev: { lat: number; lng: number } | null = null;

  for (let i = 0; i < data.length; i++) {
    const p = data[i];
    if (p.yieldAmount == null || p.timeDiff == null) continue;
    if (p.timeDiff <= 0 || p.timeDiff >= MAX_STEP_SECONDS) continue;
    if (!gates.staticPotato) {
      const speed = p.rawSpeed ?? 0;
      if (speed < gates.minSpeed || speed > maxSpeed) continue;
    }

    const kg = p.yieldAmount * (opts.coefficientOverride ?? p.coeff ?? 0);
    const width = p.widthM ?? 0;
    const areaM2 = gates.staticPotato
      ? gates.virtualSpeedMs * p.timeDiff * width
      : prev != null
        ? stepMeters(prev.lat, prev.lng, p.lat, p.lng) * width
        : 0;
    prev = { lat: p.lat, lng: p.lng };
    out[i] = { kg, areaM2 };
  }
  return out;
}

/**
 * John Deere: "we trust Deere" (2026-10-20), as the dashboard and the weight
 * calculation do (backend dashboardService scanGrainDays). Deere logs only
 * with the header down, so every point is harvest — no speed band, no
 * `0 < dt < 300 s` window (the first point counts), no flow floor. Mass is
 * Deere's kg × the coefficient (a GreenGrowth calibration, else the device
 * default); area is the step from the previous point × the width in force at
 * the point. A point without a position covers no ground, and neither does
 * the one after it (the backend's step to it is NULL).
 *
 * The points the chart inserts to break its line over a gap have no position
 * and no reading: they are not points.
 */
function computeJohnDeereYields(
  data: PanelPoint[],
  coefficientOverride: number | null | undefined
): Array<PointYield | null> {
  const out: Array<PointYield | null> = new Array(data.length).fill(null);
  let prev: PanelPoint | null = null;
  for (let i = 0; i < data.length; i++) {
    const p = data[i];
    if (p.lat == null && p.lng == null) continue;
    const kg = (p.yieldAmount ?? 0) * (coefficientOverride ?? p.coeff ?? 0);
    const areaM2 =
      prev != null && hasPosition(prev) && hasPosition(p)
        ? stepMeters(prev.lat, prev.lng, p.lat, p.lng) * (p.widthM ?? 0)
        : 0;
    prev = p;
    out[i] = { kg, areaM2 };
  }
  return out;
}
